import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Card } from '@/components/ui/Card'
import {
  HelpCircle,
  Database,
  Users,
  Building2,
  Package,
  ToggleLeft,
  BarChart3,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react'

interface HelpSection {
  id: string
  title: string
  icon: typeof HelpCircle
  content: React.ReactNode
}

// Must match TypedConfirmDialog's default safety word.
const CONFIRM_WORD = 'DELETE'

// Tags allowed inside help catalog strings, rendered by <Trans>.
const RICH_TEXT = {
  b: <strong />,
  code: <code className="bg-white px-2 py-1 rounded" />,
}

function BulletList({ prefix, keys, values, className = 'list-disc pl-5 space-y-1' }: {
  prefix: string
  keys: string[]
  values?: Record<string, string>
  className?: string
}) {
  return (
    <ul className={className}>
      {keys.map((key) => (
        <li key={key}>
          <Trans i18nKey={`${prefix}.${key}`} values={values} components={RICH_TEXT} />
        </li>
      ))}
    </ul>
  )
}

function CollapsibleSection({ section, isOpen, onToggle }: {
  section: HelpSection
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <Card>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <section.icon className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-medium text-gray-900">{section.title}</h3>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-200">
          <div className="pt-4 prose prose-sm max-w-none">
            {section.content}
          </div>
        </div>
      )}
    </Card>
  )
}

export function HelpPage() {
  const { t } = useTranslation()
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['document-management']))

  const toggleSection = (id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const dm = 'help.sections.documentManagement'

  const sections: HelpSection[] = [
    {
      id: 'overview',
      title: t('help.sections.overview.title'),
      icon: HelpCircle,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.overview.intro')}</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium">{t('help.sections.overview.importantTitle')}</p>
              <p className="text-sm text-blue-800 mt-1">{t('help.sections.overview.importantBody')}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'users',
      title: t('help.sections.users.title'),
      icon: Users,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.users.intro')}</p>
          <h4 className="font-medium text-gray-900">{t('help.sections.users.keyFeatures')}</h4>
          <BulletList prefix="help.sections.users.items" keys={['create', 'view', 'reset', 'toggle', 'delete']} />
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">{t('help.sections.users.warning')}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'businesses',
      title: t('help.sections.businesses.title'),
      icon: Building2,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.businesses.intro')}</p>
          <h4 className="font-medium text-gray-900">{t('help.sections.businesses.infoAvailable')}</h4>
          <BulletList prefix="help.sections.businesses.items" keys={['owner', 'billing', 'usage', 'status']} />
        </div>
      ),
    },
    {
      id: 'packages',
      title: t('help.sections.packages.title'),
      icon: Package,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.packages.intro')}</p>
          <h4 className="font-medium text-gray-900">{t('help.sections.packages.availableTiers')}</h4>
          <BulletList prefix="help.sections.packages.items" keys={['free', 'family', 'care_pro', 'enterprise']} />
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900">{t('help.sections.packages.warning')}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'features',
      title: t('help.sections.features.title'),
      icon: ToggleLeft,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.features.intro')}</p>
          <h4 className="font-medium text-gray-900">{t('help.sections.features.featureTypes')}</h4>
          <BulletList prefix="help.sections.features.items" keys={['global', 'user']} />
          <p className="text-sm text-gray-600">{t('help.sections.features.includes')}</p>
        </div>
      ),
    },
    {
      id: 'analytics',
      title: t('help.sections.analytics.title'),
      icon: BarChart3,
      content: (
        <div className="space-y-3">
          <p>{t('help.sections.analytics.intro')}</p>
          <h4 className="font-medium text-gray-900">{t('help.sections.analytics.availableMetrics')}</h4>
          <BulletList prefix="help.sections.analytics.items" keys={['users', 'features', 'volumes', 'trends']} />
        </div>
      ),
    },
    {
      id: 'document-management',
      title: t(`${dm}.title`),
      icon: Database,
      content: (
        <div className="space-y-4">
          <p>{t(`${dm}.intro`)}</p>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <h4 className="font-medium text-red-900">{t(`${dm}.criticalTitle`)}</h4>
            </div>
            <p className="text-sm text-red-800">{t(`${dm}.criticalBody`)}</p>
          </div>

          <h4 className="font-medium text-gray-900">{t(`${dm}.howToUse`)}</h4>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              <strong>{t(`${dm}.steps.select.title`)}</strong>
              <p className="text-sm text-gray-600 mt-1">{t(`${dm}.steps.select.body`)}</p>
            </li>
            <li>
              <strong>{t(`${dm}.steps.filters.title`)}</strong>
              <ul className="list-disc pl-5 text-sm text-gray-600 mt-1 space-y-1">
                <li>{t(`${dm}.steps.filters.age`)}</li>
                <li>{t(`${dm}.steps.filters.excludeActive`)}</li>
              </ul>
            </li>
            <li>
              <strong>{t(`${dm}.steps.preview.title`)}</strong>
              <p className="text-sm text-gray-600 mt-1">{t(`${dm}.steps.preview.body`)}</p>
            </li>
            <li>
              <strong>{t(`${dm}.steps.choose.title`)}</strong>
              <p className="text-sm text-gray-600 mt-1">{t(`${dm}.steps.choose.body`)}</p>
            </li>
            <li>
              <strong>{t(`${dm}.steps.confirm.title`)}</strong>
              <p className="text-sm text-gray-600 mt-1">
                {t(`${dm}.steps.confirm.body`, { word: CONFIRM_WORD })}
              </p>
            </li>
          </ol>

          <h4 className="font-medium text-gray-900">{t(`${dm}.safetyFeatures`)}</h4>
          <BulletList
            prefix={`${dm}.safety`}
            keys={['protected', 'excludeActive', 'preview', 'typed', 'batch', 'users', 'errors']}
            values={{ word: CONFIRM_WORD }}
            className="list-disc pl-5 space-y-1 text-sm"
          />

          <h4 className="font-medium text-gray-900">{t(`${dm}.bestPractices`)}</h4>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1 text-sm">
                {['small', 'review', 'excludeActive', 'count', 'coordinate'].map((key) => (
                  <p key={key} className="text-green-900">
                    <Trans i18nKey={`${dm}.practices.${key}`} components={RICH_TEXT} />
                  </p>
                ))}
              </div>
            </div>
          </div>

          <h4 className="font-medium text-gray-900">{t(`${dm}.collectionsAvailable`)}</h4>
          <BulletList
            prefix={`${dm}.collections`}
            keys={['users', 'businesses', 'usage_analytics', 'usage_events', 'user_features', 'subscription_packages', 'global_features']}
            className="list-disc pl-5 space-y-1 text-sm"
          />

          <h4 className="font-medium text-gray-900">{t(`${dm}.troubleshooting`)}</h4>
          <BulletList
            prefix={`${dm}.trouble`}
            keys={['noDocs', 'partial', 'preview']}
            className="list-disc pl-5 space-y-1 text-sm text-gray-600"
          />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <HelpCircle className="w-8 h-8 text-gray-700" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('help.title')}</h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('help.subtitle')}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map(section => (
          <CollapsibleSection
            key={section.id}
            section={section}
            isOpen={openSections.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
        ))}
      </div>

      <Card>
        <div className="p-6 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-3">{t('help.moreHelp.title')}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('help.moreHelp.intro')}
          </p>
          <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
            <li>{t('help.moreHelp.console')}</li>
            <li>
              <Trans
                i18nKey="help.moreHelp.docs"
                values={{ path: 'docs/DOCUMENT_MANAGEMENT.md' }}
                components={RICH_TEXT}
              />
            </li>
            <li>{t('help.moreHelp.contact')}</li>
          </ul>
        </div>
      </Card>
    </div>
  )
}
